import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Tooltip,
  TextField,
  InputAdornment,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useSearch } from './SearchContext';
import Keyvault from './Keyvault';
import config from "../../env.json"
import SearchIcon from '@mui/icons-material/Search';

interface Vault {
  ClientRsKeyVault_Nm: string;
  Id: number;
  name: string;
  type: 'common' | 'client';
  secretCount: number;
  CommonKeyVault_Nm: string;
  Domain: string;
  vaultName: string;
  originalId: number;
}

const VaultList: React.FC = () => {
  const [vaults, setVaults] = useState<Vault[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { searchText, setSearchText } = useSearch();
  const [debouncedSearchText, setDebouncedSearchText] = useState<string>('');

  // State for selected filter and filtered data
  const [filter, setFilter] = useState('all');
  const [filteredData, setFilteredData] = useState<Vault[]>([]);

  // Debounce search text
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 400);

    return () => {
      clearTimeout(timer);
    };
  }, [searchText]);

  useEffect(() => {
    const fetchVaults = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          config.REACT_APP_ENV_API_BASE + '/KeyvaultControl/getVaultListAdmin',
          {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${sessionStorage.getItem('login')}`,
            },
          }
        );

        // Transform data to create separate entries for Common and Client keyvaults
        const transformedVaults: Vault[] = [];
        response.data.data.forEach((item: any) => {
          // Add Client keyvault card
          if (item.ClientRsKeyVault_Nm) {
            transformedVaults.push({
              ...item,
              vaultName: item.ClientRsKeyVault_Nm,
              type: 'client',
              originalId: item.Id,
            });
          }

          // Add Common keyvault card
          if (item.CommonKeyVault_Nm) {
            transformedVaults.push({
              ...item,
              vaultName: item.CommonKeyVault_Nm,
              type: 'common',
              originalId: item.Id,
            });
          }
        });

        setVaults(transformedVaults);
        setLoading(false);
      } catch (error) {
        setError('Failed to load vaults');
      } finally {
        setLoading(false);
      }
    };

    fetchVaults();
  }, []);

  // Apply search filtering logic
  useEffect(() => {
    let filteredVaults: any = vaults;

    if (debouncedSearchText.length >= 3) {
      filteredVaults = filteredVaults.filter((vault: any) =>
        vault.vaultName.toLowerCase().includes(debouncedSearchText.toLowerCase()) ||
        (vault.Domain && vault.Domain.toLowerCase().includes(debouncedSearchText.toLowerCase()))
      );
    }

    // Apply the filter logic based on selected filter
    if (filter !== 'all') {
      filteredVaults = filteredVaults.filter((vault: any) => vault.type === filter);
    }

    setFilteredData(filteredVaults);
  }, [debouncedSearchText, filter, vaults]);

  const handleFilterChange = (event: SelectChangeEvent) => {
    const selectedFilter = event.target.value;
    setFilter(selectedFilter);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box textAlign="center" p={3}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <>
      <Box bgcolor="#f5f5f5" sx={{
        overflowY: 'auto',
        pr: 2,
      }}>
        <Keyvault />
        <div className='search-container' style={{textAlign : "end", position : "relative", top :"-70px"}}>
          <TextField
            variant="outlined"
            placeholder="Search.."
            size="small"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              style: {
                background: 'white',
                borderRadius: 8,
                minWidth: 300,
                width: '100%',
                maxWidth: 500,
              },
            }}
          />
        </div>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} ml={10} pl={3} width={"90%"}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 500, color: '#333' }}>
            Key Vaults
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            component={Link}
            to="/vault/new"
            sx={{ textTransform: 'none' }}
            disabled={true}
          >
            Add Vault
          </Button>
          <div>
            <FormControl variant="outlined" sx={{ minWidth: 120 }}>
              <InputLabel
                id="filter-label"
                htmlFor="filter"
                sx={{
                  color: 'black',
                  '&.Mui-focused': {
                    color: '#482880', // Label color when focused
                  },
                }}
              >
                Filter by
              </InputLabel>
              <Select
                label="Filter by"
                value={filter}
                onChange={handleFilterChange}
                id="filter"
                labelId="filter-label"
                sx={{
                  color: 'black',
                  borderRadius: '5px',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#482880',
                    borderRadius: '5px',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#482880',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#482880',
                  },
                  '& .MuiSelect-icon': {
                    color: '#482880', // Down arrow icon
                  },
                }}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="common">Common</MenuItem>
                <MenuItem value="client">Client</MenuItem>
              </Select>
            </FormControl>
          </div>

        </Box>

        <Grid container spacing={3} ml={10} width={"90%"}>
          {filteredData.length === 0 ? (
            <Grid item xs={12}>
              <Typography variant="body2" color="textSecondary">
                No record found
              </Typography>
            </Grid>
          ) : (
            filteredData.map((vault, index) => (
              <Grid item xs={12} sm={6} md={4} key={`${vault.originalId}-${vault.type}-${index}`}>
                <Card
                  component={Link}
                  to={`/keyvault/${vault.vaultName}/${vault.originalId}`}
                  sx={{
                    textDecoration: 'none',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    '&:hover': {
                      boxShadow: 3,
                    },
                  }}
                >
                  <CardContent>
                    <Typography variant="h6" component="h2" gutterBottom>
                      {vault.vaultName}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: vault.type === 'common' ? '#1976d2' : '#2e7d32',
                        textTransform: 'uppercase',
                        mb: 1
                      }}
                    >
                      {vault.type}
                    </Typography>
                    <Tooltip title={vault.Domain} arrow placement="top">
                      <Typography variant="body2" color="textSecondary" noWrap>
                        {vault.Domain}
                      </Typography>
                    </Tooltip>
                  </CardContent>
                </Card>
              </Grid>
            ))
          )}
        </Grid>
      </Box>
    </>
  );
};

export default VaultList;
